import React from 'react';

import { colors } from '../../../theme';
import brandCodes from '../../../constants/brandCodes';

import { LoaderContainer, LoaderSpinner } from '../LoaderSpinner';

import { BookingSummaryProps } from './BookingSummary.interface';
import * as S from './BookingSummary.style';
import { DateSummary } from './TravelSummary/DateSummary';
import { groupCodeLogoMapping } from '../../Images/Images.constants';
import { BookingSummaryCategory } from './BookingSummaryCategory';
import { BookingSummaryPaymentSection } from './BookingSummaryPaymentSection';
import { BookingSummaryLoyaltyPrograms } from './BookingSummaryLoyaltyPrograms';

export const BookingSummary = ({
  countryCode,
  reference,
  imageUrl,
  group,
  summary,
  loading,
  hideDynamicLiability,
  onDiscountInfoClick,
  ...dateSummaryProps
}: BookingSummaryProps) => {
  const groupCode = group?.code;
  const GroupLogo = groupCodeLogoMapping[groupCode];

  if (loading && !summary) {
    return (
      <S.Container>
        <S.LoaderContainer>
          <LoaderSpinner />
        </S.LoaderContainer>
      </S.Container>
    );
  }

  if (!summary) {
    return <S.Container>Error</S.Container>;
  }

  const {
    vehicle,
    insurance,
    bookingTotalPrice,
    extras,
    currency,
    isExternalAgent,
    loyaltyPrograms,
  } = summary;

  const insuranceTitle =
    [brandCodes.RoadBear, brandCodes.MonteRV].indexOf(groupCode) >= 0
      ? 'Insurance & Coverages'
      : 'Accident liability';

  return (
    <S.Container data-test-id="b2c-booking-summary">
      <LoaderContainer
        loading={loading}
        opacity={{ opacity: '90%', color: colors.b2cWhite }}
      >
        <S.Header>
          {!!GroupLogo && (
            <S.LogoContainer>
              <GroupLogo />
            </S.LogoContainer>
          )}
          <S.CenteredWrapper leftPadding={!!GroupLogo}>
            <S.HeaderText>BOOKING NUMBER:</S.HeaderText>
            <br />
            <S.BookingRef>{reference}</S.BookingRef>
          </S.CenteredWrapper>
        </S.Header>
        <S.ClassContainer>
          <S.CenteredWrapper leftPadding>
            <S.ClassSpan>{vehicle?.productPrice?.name}</S.ClassSpan>
          </S.CenteredWrapper>
          <S.ClassImageWrapper>
            <S.ClassImage src={imageUrl} />
          </S.ClassImageWrapper>
        </S.ClassContainer>
        <DateSummary {...dateSummaryProps} />
        <S.BookingSummaryCategoriesContainer>
          <BookingSummaryCategory
            title={'Vehicle rental'}
            showDetailedPrice
            onDiscountInfoClick={onDiscountInfoClick}
            currency={currency}
            {...vehicle}
          />
          <S.DashLine />
          <BookingSummaryCategory
            title={insuranceTitle}
            onDiscountInfoClick={onDiscountInfoClick}
            currency={currency}
            {...insurance}
          />
          <S.Line />
          {extras?.productPrices?.length > 0 && (
            <>
              <BookingSummaryCategory
                title={'Extras'}
                onDiscountInfoClick={onDiscountInfoClick}
                currency={currency}
                showQuantity
                {...extras}
              />
              <S.Line />
            </>
          )}
          <BookingSummaryPaymentSection
            totalPrice={bookingTotalPrice}
            countryCode={countryCode}
            currency={currency}
            isExternalAgent={isExternalAgent}
            hideDynamicLiability={hideDynamicLiability}
          />
        </S.BookingSummaryCategoriesContainer>
        {!!loyaltyPrograms?.length && (
          <S.BookingSummaryCategoriesContainer>
            <S.Line />
            <BookingSummaryLoyaltyPrograms loyaltyPrograms={loyaltyPrograms} />
          </S.BookingSummaryCategoriesContainer>
        )}
      </LoaderContainer>
    </S.Container>
  );
};
